import { DescriptionBar, SearchBar, Button, Modal, ModalInner } from 'components';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // [!code ++]
import { useSearchParams } from 'react-router-dom';
import ScheduleRecipientsTab from './components/ScheduleRecipientsTab';
import PaymentRecipientsTab from './components/PaymentRecipientsTab';
import InquiryRecipientsTab from './components/InquiryRecipientsTab';
import AddRecipientModal from './components/AddRecipientModal';
import MailCategoryGuide from './components/MailCategoryGuide';
import MenuTabSecond from 'components/MenuTabSecond';
import { CashOutlineIcon, UserOutlineIcon, ViewListOutlineIcon, PlusOutlineIcon } from 'assets/svgs';
import axios from 'common/AxiosAuth';
import { RecipientsItem } from './type';

// * 탭 인덱스 정의
const MENU_INDEX = {
  SCHEDULE: 0,
  PAYMENT: 1,
  INQUIRY: 2
};

const ManageMail = () => {
  const { t } = useTranslation(); // [!code ++]
  const buttonMenuList = [ // [!code ++]
    t('manageMail.tabs.schedule'), // [!code ++]
    t('manageMail.tabs.payment'), // [!code ++]
    t('manageMail.tabs.inquiry') // [!code ++]
  ]; // [!code ++]
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(() => {
    const tab = searchParams.get('tab');
    return tab ? parseInt(tab) : MENU_INDEX.SCHEDULE;
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [isAddModalOpened, setIsAddModalOpened] = useState<boolean>(false);
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [deleteModalData, setDeleteModalData] = useState<any[] | null>(null);
  const [selectedRecipientsData, setSelectedRecipientsData] = useState<RecipientsItem[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // * 탭 클릭 핸들러
  function onClickTab(index: number) {
    setSelectedMenuIndex(index);
    setSearchParams({ tab: index.toString() });
    setCheckedRows([]); // * 탭 변경 시 체크박스 상태 초기화
  }

  const handleAddRecipient = () => {
    setIsAddModalOpened(true);
  };

  const handleRecipientAdded = async () => {
    // * 모든 탭에서 새로고침
    setRefreshKey((prev) => prev + 1);
    setCheckedRows([]);
    setSelectedRecipientsData([]);
  };

  // * 삭제 모달 데이터 설정
  const onClickDelete = (selectedData?: RecipientsItem[]) => {
    if (selectedData && selectedData.length > 0) {
      setDeleteModalData(selectedData);
    } else if (checkedRows.length > 0) {
      setDeleteModalData(checkedRows.map((id) => ({ receiverId: id } as RecipientsItem)));
    }
  };

  // * 삭제 실행 함수
  const onDelete = async () => {
    if (deleteModalData && deleteModalData.length > 0) {
      try {
        await axios.delete('/api/admin/mail/receiver/delete', {
          data: deleteModalData.map((item) => item.receiverId)
        });
        setDeleteModalData(null);
        setCheckedRows([]);
        setSelectedRecipientsData([]);
        setRefreshKey((prev) => prev + 1); // * 모든 탭에서 새로고침
      } catch (error) {
        console.error('수신자 삭제 실패:', error);
      }
    }
  };

  const onCancelDelete = () => {
    setDeleteModalData(null);
  };

  // * 선택된 데이터 변경 핸들러
  const handleSelectionChange = useCallback((selectedData: RecipientsItem[]) => {
    setSelectedRecipientsData(selectedData);
  }, []);

  // * 삭제 버튼 클릭 핸들러
  const handleDeleteClick = () => {
    if (selectedRecipientsData.length > 0) {
      setDeleteModalData(selectedRecipientsData);
    } else {
      onClickDelete();
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('manageMail.pageTitle')}
        description={t('manageMail.description')}
      />

      <div className="w-full flex-1 flex flex-col">
        <div className="flex flex-row items-center justify-between p-2">
          <div className="flex flex-row gap-2"></div>
        </div>
        <MenuTabSecond
          menuList={buttonMenuList}
          selectedMenuIndex={selectedMenuIndex}
          onClickTab={onClickTab}
          icons={[
            <ViewListOutlineIcon key="view-list" className="w-5 h-5" />,
            <CashOutlineIcon key="cash" className="w-5 h-5" />,
            <UserOutlineIcon key="user" className="w-5 h-5" />
          ]}
        />
        <MailCategoryGuide />
        {selectedMenuIndex === MENU_INDEX.SCHEDULE && (
          <>
            <div className="flex flex-row flex-wrap items-center justify-between gap-4 p-6">
              <div className="ml-12">
                <SearchBar
                  className="w-full md:w-60"
                  value={searchValue}
                  setValue={setSearchValue}
                  placeholder={t('manageMail.searchPlaceholder')}
                  aria-label="Search users"
                />
              </div>
              <div className="flex w-full flex-row justify-end gap-2 md:w-fit">
                <Button
                  className="h-8"
                  type="secondary"
                  text={t('manageMail.buttons.delete')}
                  onClick={handleDeleteClick}
                  disabled={!checkedRows.length}
                  aria-label="delete selected recipients"
                />
                <Button
                  className="h-8"
                  type="blue"
                  text={t('manageMail.buttons.add')}
                  onClick={handleAddRecipient}
                  icon={<PlusOutlineIcon className="[&_path]:stroke-white" />}
                  aria-label="add recipient"
                />
              </div>
            </div>
            <div className="flex-1">
              <ScheduleRecipientsTab
                key={refreshKey}
                searchValue={searchValue}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                onDelete={onClickDelete}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          </>
        )}
        {selectedMenuIndex === MENU_INDEX.PAYMENT && (
          <>
            <div className="flex flex-row flex-wrap items-center justify-between gap-4 p-6">
              <div className="ml-12">
                <SearchBar
                  className="w-full md:w-60"
                  value={searchValue}
                  setValue={setSearchValue}
                  placeholder={t('manageMail.searchPlaceholder')}
                  aria-label="Search users"
                />
              </div>
              <div className="flex w-full flex-row justify-end gap-2 md:w-fit">
                <Button
                  className="h-8"
                  type="secondary"
                  text={t('manageMail.buttons.delete')}
                  onClick={handleDeleteClick}
                  disabled={!checkedRows.length}
                  aria-label="delete selected recipients"
                />
                <Button
                  className="h-8"
                  type="blue"
                  text={t('manageMail.buttons.add')}
                  onClick={handleAddRecipient}
                  icon={<PlusOutlineIcon className="[&_path]:stroke-white" />}
                  aria-label="add recipient"
                />
              </div>
            </div>
            <div className="flex-1">
              <PaymentRecipientsTab
                key={refreshKey}
                searchValue={searchValue}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                onDelete={onDelete}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          </>
        )}
        {selectedMenuIndex === MENU_INDEX.INQUIRY && (
          <>
            <div className="flex flex-row flex-wrap items-center justify-between gap-4 p-6">
              <div className="ml-12">
                <SearchBar
                  className="w-full md:w-60"
                  value={searchValue}
                  setValue={setSearchValue}
                  placeholder={t('manageMail.searchPlaceholder')}
                  aria-label="Search users"
                />
              </div>
              <div className="flex w-full flex-row justify-end gap-2 md:w-fit">
                <Button
                  className="h-8"
                  type="secondary"
                  text={t('manageMail.buttons.delete')}
                  onClick={handleDeleteClick}
                  disabled={!checkedRows.length}
                  aria-label="delete selected recipients"
                />
                <Button
                  className="h-8"
                  type="blue"
                  text={t('manageMail.buttons.add')}
                  onClick={handleAddRecipient}
                  icon={<PlusOutlineIcon className="[&_path]:stroke-white" />}
                  aria-label="add recipient"
                />
              </div>
            </div>
            <div className="flex-1">
              <InquiryRecipientsTab
                key={refreshKey}
                searchValue={searchValue}
                checkedRows={checkedRows}
                setCheckedRows={setCheckedRows}
                onDelete={onDelete}
                onSelectionChange={handleSelectionChange}
              />
            </div>
          </>
        )}
      </div>
      <AddRecipientModal
        isOpened={isAddModalOpened}
        setIsOpened={setIsAddModalOpened}
        onRecipientAdded={handleRecipientAdded}
      />

      {/* Delete Modal */}
      <Modal isOpened={!!deleteModalData}>
        <ModalInner
          className="md:w-[400px]"
          title={t('manageMail.deleteModal.title')}
          onClickClose={onCancelDelete}
        >
          <p className="p-4 pt-0 text-body1" style={{ whiteSpace: 'pre-wrap' }}>
            {t('manageMail.deleteModal.body')}
          </p>
          <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
            <Button
              className="h-9"
              text={t('manageMail.buttons.cancel')}
              onClick={onCancelDelete}
              type="secondary"
              size="large"
            />
            <Button
              className="h-9 bg-red-600 border-red-600"
              text={t('manageMail.buttons.delete')}
              onClick={onDelete}
              type="blue"
              size="large"
            />
          </div>
        </ModalInner>
      </Modal>
    </div>
  );
};

export default ManageMail;